import type { DocumentoExportable, FilaExportable } from "@/componentes/exportar/modelo"

import { type Cargo, type Departamento, etiquetaDeNivel } from "./cargo"

/**
 * El catálogo de cargos y el de departamentos como documentos exportables (29/10).
 *
 * ⚠ **Las dos pantallas son listas de tarjetas, no tablas**, así que se arman con los
 * datos ya cargados: lo mismo que se ve, con las cuentas como números.
 */

export function documentoDeCargos(cargos: Cargo[]): DocumentoExportable {
  const activos = cargos.filter((c) => c.activo).length
  return {
    titulo: "Catálogo de cargos",
    subtitulo: `${cargos.length} cargos · ${activos} activos`,
    secciones: [
      {
        titulo: "Cargos",
        columnas: [
          { titulo: "Cargo", tipo: "texto" },
          { titulo: "Departamento", tipo: "texto" },
          { titulo: "Nivel", tipo: "texto" },
          { titulo: "Reporta a", tipo: "texto" },
          { titulo: "Personas", tipo: "numero" },
          { titulo: "Le reportan", tipo: "numero" },
          { titulo: "Funciones", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
        ],
        filas: cargos.map((c) => ({
          celdas: [
            c.nombre,
            c.departamento_nombre ?? "Sin departamento",
            etiquetaDeNivel(c.nivel),
            c.reporta_a_nombre,
            c.personas,
            c.le_reportan,
            c.funciones.join(" · ") || null,
            c.activo ? "Activo" : "Retirado",
          ],
        })),
      },
    ],
  }
}

export function documentoDeDepartamentos(
  departamentos: Departamento[],
  cargos: Cargo[] | undefined,
  conArchivados: boolean,
): DocumentoExportable {
  const puestos: FilaExportable[] = []
  for (const d of departamentos) {
    const suyos = (cargos ?? []).filter((c) => c.departamento_id === d.id)
    if (suyos.length === 0) continue
    puestos.push({ celdas: [d.nombre, null, null, null], grupo: true })
    for (const c of suyos) {
      puestos.push({
        celdas: [c.nombre, etiquetaDeNivel(c.nivel), c.personas, c.activo ? "Activo" : "Retirado"],
      })
    }
  }
  const sinArea = (cargos ?? []).filter((c) => c.departamento_id === null)
  if (sinArea.length > 0) {
    puestos.push({ celdas: ["Sin departamento", null, null, null], grupo: true })
    for (const c of sinArea) {
      puestos.push({
        celdas: [c.nombre, etiquetaDeNivel(c.nivel), c.personas, c.activo ? "Activo" : "Retirado"],
      })
    }
  }

  return {
    titulo: "Departamentos",
    subtitulo: conArchivados ? "Activos y archivados" : "Departamentos activos",
    secciones: [
      {
        titulo: "Departamentos",
        columnas: [
          { titulo: "Código", tipo: "texto" },
          { titulo: "Siglas", tipo: "texto" },
          { titulo: "Departamento", tipo: "texto" },
          { titulo: "Depende de", tipo: "texto" },
          { titulo: "Cargo responsable", tipo: "texto" },
          { titulo: "Ocupante", tipo: "texto" },
          { titulo: "Cargos", tipo: "numero" },
          { titulo: "Estado", tipo: "texto" },
        ],
        filas: departamentos.map((d) => ({
          celdas: [
            d.codigo,
            d.siglas,
            d.nombre,
            d.padre ?? "Dirección",
            d.cargo_responsable,
            d.responsable_ocupante
              ? `${d.responsable_ocupante}${d.responsable_interino ? " (interino)" : ""}`
              : null,
            d.cargos,
            d.activo ? "Activo" : "Archivado",
          ],
        })),
      },
      ...(puestos.length > 0
        ? [
            {
              titulo: "Puestos por departamento",
              columnas: [
                { titulo: "Cargo", tipo: "texto" as const },
                { titulo: "Nivel", tipo: "texto" as const },
                { titulo: "Personas", tipo: "numero" as const },
                { titulo: "Estado", tipo: "texto" as const },
              ],
              filas: puestos,
            },
          ]
        : []),
    ],
  }
}
